import React, {
  createContext,
  type ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';

import { App } from 'antd';
import type { CreateTagPayload } from 'interfaces/FileApiService';
import { Trans, useTranslation } from 'react-i18next';
import { TagForm, type TagFormTabKey } from '../../components/Forms/TagForm';
import { useAuth } from '../../hooks/useAuth';
import { useOpenGuardedFormModal } from '../../hooks/useOpenGuardedFormModal';
import type Tag from '../../interfaces/Tag';
import { authService } from '../../services/authService';
import { FileService } from '../../services/fileApiService';
import { useAppStore } from '../../store/store';
import { hasFileAccessScope } from '../../utils/jwt';

export type OpenTagFormOptions = {
  initialTab?: TagFormTabKey;
};

interface TagsContextType {
  tags: Tag[];
  isLoadingTags: boolean;
  selectedTags: Tag[];
  setSelectedTags: (tags: Tag[] | ((prev: Tag[]) => Tag[])) => void;
  handleOpenTagForm: (tag: Tag | null, options?: OpenTagFormOptions) => void;
  handleDeleteTag: (tag: Tag) => void;
  createTag: (tag: Tag) => void;
  updateTag: (tag: Tag) => void;
}

export const TagsContext = createContext<TagsContextType>({
  tags: [],
  isLoadingTags: false,
  selectedTags: [],
  setSelectedTags: () => {},
  handleOpenTagForm: () => {},
  handleDeleteTag: () => {},
  createTag: () => {},
  updateTag: () => {},
});

const fileService = new FileService();

interface TagsProviderProps {
  children: ReactNode;
}

export const TagsProvider: React.FC<TagsProviderProps> = ({ children }) => {
  const { authenticatedUser } = useAuth();
  const { t } = useTranslation();
  const { modal, notification } = App.useApp();
  const openGuardedFormModal = useOpenGuardedFormModal();

  // Get tags from Zustand store instead of local state
  const tags = useAppStore((state) => state.tags);
  const setTags = useAppStore((state) => state.setTags);

  const [isLoadingTags, setIsLoadingTags] = useState(false);
  const [selectedTags, setSelectedTagsState] = useState<Tag[]>([]);

  // Wrapper to support both direct value and functional update
  const setSelectedTags = useCallback((tags: Tag[] | ((prev: Tag[]) => Tag[])) => {
    if (typeof tags === 'function') {
      setSelectedTagsState(tags);
    } else {
      setSelectedTagsState(tags);
    }
  }, []);

  /**
   * @description Get the tags.
   *
   */
  const getTags = useCallback(() => {
    // File-scoped OTP / anticapture sessions: /myfiles/tags not on allow-list → 403 DENIED.
    if (hasFileAccessScope(authService.getToken())) {
      return;
    }

    setIsLoadingTags(true);
    fileService
      .getTags()
      .then((response) => {
        setTags(response);
      })
      .catch(() => {
        notification.error({
          title: t('errors.hitASnag'),
          showProgress: true,
          pauseOnHover: true,
        });
      })
      .finally(() => {
        setIsLoadingTags(false);
      });
  }, []);

  /**
   * @description Create a tag.
   * @param {Tag} tag - The tag to create.
   */
  const createTag = useCallback(
    (tag: Tag) => {
      const payload: CreateTagPayload = {
        color: tag.color,
        publicTag: tag.publicTag,
        title: tag.title,
        fileVisible: tag.fileVisible,
        contributor: tag.contributor,
        reader: tag.reader,
        external: tag.external,
        userIds: tag.userIds,
        groupIds: tag.groupIds,
      };

      setIsLoadingTags(true);
      fileService
        .createTag(payload)
        .then(() => {
          getTags();
        })
        .catch(() => {
          notification.error({
            title: t('errors.hitASnag'),
            showProgress: true,
            pauseOnHover: true,
          });
        })
        .finally(() => {
          setIsLoadingTags(false);
        });
    },
    [getTags, authenticatedUser?.id],
  );

  /**
   * @description Update a tag.
   * @param {Tag} tag - The tag to update.
   */
  const updateTag = useCallback(
    (tag: Tag) => {
      setIsLoadingTags(true);
      const payload: Tag = {
        id: tag.id,
        title: tag.title,
        color: tag.color,
        publicTag: tag.publicTag,
        fileVisible: tag.fileVisible,
        contributor: tag.contributor,
        reader: tag.reader,
        external: tag.external,
        userIds: tag.userIds,
        groupIds: tag.groupIds,
        usersAndGroupsCount: tag.usersAndGroupsCount,
        isUsedForFileVisibility: tag.isUsedForFileVisibility,
      };
      fileService
        .createTag(payload)
        .then((response: Tag) => {
          // Update the tag in the existing tags array instead of reloading all tags
          setTags(tags.map((t) => (t.id === response.id ? response : t)));
        })
        .catch(() => {
          notification.error({
            title: t('errors.hitASnag'),
            showProgress: true,
            pauseOnHover: true,
          });
        })
        .finally(() => {
          setIsLoadingTags(false);
        });
    },
    [tags, setTags, t, notification],
  );

  /**
   * @description Open the tag form.
   * @param {Tag | null} tag - The tag to edit.
   * @param {OpenTagFormOptions} [options] - Optional initial tab (Access & Protections only when editing).
   */
  const handleOpenTagForm = (tag: Tag | null, options?: OpenTagFormOptions) => {
    const modalInstance = openGuardedFormModal({
      content: (
        <TagForm
          tag={tag}
          initialTab={options?.initialTab}
          onSubmit={(newTag) => {
            if (newTag.id === 0) {
              createTag(newTag);
            } else {
              updateTag(newTag);
            }
            modalInstance.destroy();
          }}
          onCancel={() => {
            modalInstance.destroy();
          }}
        />
      ),
      title: tag ? t('modal.tagForm.editTitle') : t('modal.tagForm.newTitle'),
    });
  };

  /**
   * @description Convert SNAKE_CASE setting name to camelCase for translation key.
   * @param {string} settingName - The setting name in SNAKE_CASE.
   * @returns {string} The setting name in camelCase.
   */
  const settingNameToCamelCase = (settingName: string): string => {
    return settingName.toLowerCase().replace(/_([a-z])/g, (_, letter) => letter.toUpperCase());
  };

  /**
   * @description Get translated setting names from API setting keys.
   * @param {string[]} settings - Array of setting names in SNAKE_CASE.
   * @returns {string} Comma-separated translated setting names.
   */
  const getTranslatedSettingNames = (settings: string[]): string => {
    return settings
      .map((setting) => {
        const camelCaseKey = settingNameToCamelCase(setting);
        const translationKey = `settings.${camelCaseKey}.title`;
        const translated = t(translationKey);
        // If translation not found, return the original setting name formatted
        return translated !== translationKey
          ? translated
          : setting.replace(/_/g, ' ').toLowerCase();
      })
      .join(', ');
  };

  /**
   * @description Delete a tag.
   * @param {Tag} tag - The tag to delete.
   */
  const showTagInUseError = (error: any) => {
    const { settings = [], classificationAgent = false } = error?.response || {};

    const usageItems: { label: string; details?: string }[] = [];

    if (settings.length > 0) {
      const translatedSettings = getTranslatedSettingNames(settings);
      usageItems.push({
        label: t('modal.deleteTag.usedInSettings'),
        details: translatedSettings,
      });
    }

    if (classificationAgent) {
      usageItems.push({
        label: t('modal.deleteTag.usedInClassificationAgent'),
      });
    }

    modal.error({
      title: t('modal.deleteTag.errorInUse'),
      content: (
        <div>
          <p style={{ marginBottom: '12px' }}>{t('modal.deleteTag.errorInUseDescription')}</p>
          {usageItems.length > 0 && (
            <ul
              style={{
                margin: '0 0 16px 0',
                paddingLeft: '20px',
                listStyleType: 'disc',
              }}
            >
              {usageItems.map((item, index) => (
                <li key={index} style={{ marginBottom: '4px' }}>
                  <strong>{item.label}</strong>
                  {item.details && <span style={{ color: '#666' }}> ({item.details})</span>}
                </li>
              ))}
            </ul>
          )}
          <p
            style={{
              margin: 0,
              padding: '8px 12px',
              backgroundColor: '#f5f5f5',
              borderRadius: '4px',
              fontSize: '13px',
            }}
          >
            {t('modal.deleteTag.errorInUseAction')}
          </p>
        </div>
      ),
      centered: true,
    });
  };

  const handleDeleteTag = (tag: Tag) => {
    modal.confirm({
      title: t('modal.deleteTag.title'),
      icon: null,
      centered: true,
      content: (
        <Trans>
          <p>
            {tag.isUsedForFileVisibility
              ? t('modal.deleteTag.descriptionWithPublicTag', {
                  resource: tag.title,
                })
              : t('modal.deleteTag.description', {
                  resource: tag.title,
                })}
          </p>
        </Trans>
      ),
      onOk: async () => {
        setIsLoadingTags(true);
        try {
          await fileService.deleteTag(tag.id);
          getTags();
        } catch (error: any) {
          console.error(
            '[deleteTag] caught error:',
            error,
            'status:',
            error?.status,
            'response:',
            error?.response,
          );
          if (error?.status === 409) {
            showTagInUseError(error);
          } else {
            modal.error({
              title: t('errors.hitASnag'),
              centered: true,
            });
          }
        } finally {
          setIsLoadingTags(false);
        }
      },
      onCancel: () => {},
    });
  };

  /**
   * @description Get the tags on component mount.
   */
  useEffect(() => {
    if (authenticatedUser?.id) {
      getTags();
    }
  }, [authenticatedUser?.id]);

  const contextValue = useMemo(() => {
    return {
      tags,
      isLoadingTags,
      selectedTags: selectedTags,
      setSelectedTags,
      handleOpenTagForm,
      createTag,
      updateTag,
      handleDeleteTag,
    };
  }, [
    tags,
    isLoadingTags,
    selectedTags,
    setSelectedTags,
    handleOpenTagForm,
    createTag,
    updateTag,
    handleDeleteTag,
  ]);

  return <TagsContext.Provider value={contextValue}>{children}</TagsContext.Provider>;
};
